import { Fragment } from 'react/jsx-runtime';

/** Material UI Packages. */
import { Link, Stack, Typography } from '@mui/material';
import { GridFilterInputValue } from '@mui/x-data-grid-pro';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { ocean_freight_method, ocean_freight_accounting } from 'shared/config/constantsData';
import { CustomSelectCell_string, DateTypingEditCell } from 'shared/ui/RenderCell';
import { createQuickFilterFn_MMDDYY } from 'shared/lib/quickFilterUtils';
import { parseDateToLocal } from 'shared/lib/dateFormatter';
import { createOptionPasteParser, parsePastedDate } from 'shared/lib/datagridHelpers';

export const getColumn = (onInvalidOption) => [
    {
        field: 'id',
        headerName: 'ID',
        align: 'center',
        headerAlign: 'center',
    },
    {
        field: 'il_opus_reference_id',
        headerName: 'OPUS Ref',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        renderCell: (params) => {
            return <Typography sx={{ ...customized_style.itemDatagrid_Typography }}>{params.value}</Typography>;
        },
    },
    {
        field: 'client',
        headerName: 'Client',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        valueGetter: (params) => {
            return params;
        },
        valueFormatter: (params) => {
            return params ? params?.name : '';
        },
        groupingValueGetter: (value) => {
            return value?.name || '';
        },
        renderCell: (params) => {
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {params.value?.name || params.value}
                </Typography>
            );
        },
        filterOperators: [
            {
                label: 'contains',
                value: 'contains',
                InputComponent: GridFilterInputValue,
                getApplyFilterFn: (filterItem) => {
                    if (!filterItem.value) {
                        return null;
                    }
                    return (value) => {
                        const lowerCaseValue = value.name?.toString().toLowerCase();
                        const lowerCaseFilterValue = filterItem.value.toLowerCase();
                        return lowerCaseValue?.includes(lowerCaseFilterValue);
                    };
                },
            },
        ],
    },
    {
        field: 'mbl',
        headerName: 'MBL/MAWB',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
    },
    {
        field: 'hbl',
        headerName: 'HBL/HAWB',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        renderCell: (params) => {
            const hblList = Array.isArray(params.value) ? params.value : [];
            return (
                <Stack sx={customized_style.itemDatagrid_Typography}>
                    {hblList.map((hbl, index) => (
                        <div key={index}>{hbl}</div>
                    ))}
                </Stack>
            );
        },
    },
    {
        field: 'volume',
        headerName: 'Volume',
        align: 'center',
        headerAlign: 'center',
        flex: 1,
        renderCell: (params) => {
            const stringValue = params.value?.split('\n').filter((value) => value !== '') || [];
            return (
                <Typography sx={customized_style.itemDatagrid_Typography}>
                    {stringValue.map((line, index) => (
                        <Fragment key={index}>
                            {line}
                            <br />
                        </Fragment>
                    ))}
                </Typography>
            );
        },
    },
    {
        field: 'eta',
        headerName: 'ETA',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (params) => {
            const date = parseDateToLocal(params);
            if (!date) return null;
            // MM/DD/YY format search.
            return date.toLocaleDateString('en-US', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
            });
        },
        renderCell: (params) => {
            if (params.value != null) {
                return <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>;
            } else return null;
        },
    },
    {
        field: 'feta',
        headerName: 'F.ETA',
        flex: 1,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (params) => {
            const date = parseDateToLocal(params);
            if (!date) return null;
            // MM/DD/YY format search.
            return date.toLocaleDateString('en-US', {
                year: '2-digit',
                month: '2-digit',
                day: '2-digit',
            });
        },
        renderCell: (params) => {
            if (params.value != null) {
                return <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>;
            } else return null;
        },
    },
    {
        field: 'freight_link',
        headerName: 'Collect Charge',
        align: 'center',
        headerAlign: 'center',
        flex: 0.7,
        disableExport: true,
        renderCell: (params) => {
            const freightLink = params.value;
            return (
                <Stack sx={customized_style.itemDatagrid_Typography}>
                    <Link href={freightLink ? freightLink : '#'} target="_blank" rel="noopener">
                        {freightLink ? 'View File' : '\u00A0'}
                    </Link>
                </Stack>
            );
        },
    },
    {
        field: 'freight',
        headerName: 'Status',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        pastedValueParser: createOptionPasteParser(ocean_freight_accounting, onInvalidOption),
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => <CustomSelectCell_string params={params} options={ocean_freight_accounting} />,
    },
    {
        field: 'paid_with',
        headerName: 'Pay Method',
        type: 'singleSelect',
        flex: 1,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        pastedValueParser: createOptionPasteParser(ocean_freight_method, onInvalidOption),
        renderCell: (params) => <Typography sx={customized_style.itemDatagrid_Typography}>{params.value}</Typography>,
        renderEditCell: (params) => <CustomSelectCell_string params={params} options={ocean_freight_method} />,
    },
    {
        field: 'paid_on',
        headerName: 'Date',
        type: 'date',
        pastedValueParser: parsePastedDate,
        flex: 1,
        editable: true,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'custom-editable-header',
        valueGetter: (params) => {
            return parseDateToLocal(params);
        },
        valueFormatter: (params) => {
            const date = parseDateToLocal(params);
            if (date) {
                return date.toLocaleDateString('en-US');
            }
            return params;
        },
        renderCell: (params) => {
            if (params.value != null) {
                const date = parseDateToLocal(params.value);
                if (date) {
                    const formattedDate = date.toLocaleDateString('en-US', {
                        year: '2-digit',
                        month: '2-digit',
                        day: '2-digit',
                    });
                    return <Typography sx={customized_style.itemDatagrid_Typography}>{formattedDate}</Typography>;
                }
            }
            return null;
        },
        renderEditCell: (params) => <DateTypingEditCell params={params} />,
        getApplyQuickFilterFn: createQuickFilterFn_MMDDYY,
    },
];
